import { useCallback, useEffect, useRef } from "react";
import dynamic from "next/dynamic";

import { Button, SVGSprite } from "@/components/Atoms";
import Typography from "@/foundation/Typography/V2";

import { classNameGenerator, clsx } from "@/utils";

import { useAppDispatch, useAppSelector } from "@/hooks";
import * as actions from "@/redux/BootCampReducer";

import styles from "./FloatingBanner.module.scss";

const OfferCountdown = dynamic(() => import("../Shared/OfferCountdown"), {
  ssr: false,
});

import OfferCountdownNew from "../Shared/OfferCountdownNew"

interface FloatingBannerProps { }

const FloatingBanner: React.FC<FloatingBannerProps> = () => {
  const dispatch = useAppDispatch();

  const cls = classNameGenerator(styles);

  const setShowBookSlotModal = useCallback(
    (value: any) => {
      dispatch(actions.setShowBookSlotModal(value));
    },
    [dispatch]
  );

  const { data } = useAppSelector((state) => state.bootCamp);
  const { label, offerEndDate, icon } = data?.heroSection?.offerSection || {};
  const { slug } = data
  const  {hideBenefitSectionClassWeb} = data?.heroSection
  const { title, ctaText, showTitleInMobile=false, hidePricingInMobile=false } = data?.floatingBanner || {};
  let { originalPrice, price, currencySymbol, startDate } =
    data?.packData || {};

  const floatingBannerRef = useRef<HTMLDivElement>(null!);
  const tncEnabled = data?.termsAndCondition && true
  const isTtcPage = data?.conditionKey === "yoga-trainer-foundation-program"

  useEffect(() => {
    const floatingBanner = floatingBannerRef.current;
    const footer = document.getElementById("footer")!;

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const callback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          floatingBanner.classList.add("hide");
        } else {
          floatingBanner.classList.remove("hide");
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    observer.observe(footer);

    return () => observer.disconnect();
  }, [floatingBannerRef]);

  return (
    <div ref={floatingBannerRef} className={clsx(cls("floatingBanner"),cls(hideBenefitSectionClassWeb))}>
      <div className={cls("container")}>
        {title ? (
          <Typography
            variant="paragraph1"
            className={cls("title")}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
        ) : (
          <div className={cls("countdownTimer")}>
            {isTtcPage
              ? <OfferCountdownNew
                label={label}
                endDate={offerEndDate}
                className={cls("floatingBannerTimer")}
                icon={icon}
              />
              : <OfferCountdown
                label={label}
                endDate={offerEndDate}
                className={cls("floatingBannerTimer")}
                icon={icon}
              />
            }
          </div>
        )}
        <div className={cls("pricingWrapper")}>
          {showTitleInMobile && (
            <Typography
            variant="paragraph1"
            className={cls("mobileTitle")}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          )}
          {!hidePricingInMobile && <div className={cls("pricing")}>
            {originalPrice !== null && currencySymbol && <Typography
              variant="paragraph1"
              className={cls("strikeoutPrice")}
              dangerouslySetInnerHTML={{
                __html: `${currencySymbol}${originalPrice}`,
              }}
            />}
            {price !== null && <Typography
              variant="paragraph1"
              className={cls("price")}
              dangerouslySetInnerHTML={{
                __html: price > 0 ? `${currencySymbol}${price}` : "Free!",
              }}
            />}
          </div>}
          <div className={cls("actions")}>
            {startDate && (
              <Typography
                variant="paragraph1"
                className={cls("startDate")}
                dangerouslySetInnerHTML={{
                  __html: `Programs starts on<br/>${startDate}`,
                }}
              />
            )}
            <Button
              className={cls("actionButton")}
              onClick={() =>
                setShowBookSlotModal({
                  packageData: data?.packData,
                  showSubtitle: true,
                  showTnc: { tncEnabled }
                })
              }
            >
              {ctaText}
              <SVGSprite
                icon="icon-bootcamp-arrow-right"
                className={cls("icon")}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingBanner;
